import { createFileRoute } from "@tanstack/react-router";
import { RoomDetail } from "@/components/room-detail";
import { RoomDetailSkeleton } from "@/components/room-detail/room-detail-skeleton";
import { ScrollableContainer } from "@/components/ui/scrollbar";
import { roomQueryOptions } from "@/queries/use-rooms";
import { useTranslation } from "react-i18next";
import { ResourceError } from "@/components/ui/resource-error";
import { RouterContext } from "@/types/router";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import { PROTECTED_ROUTES } from "@/utils/navigation";

interface LoaderParams {
  params: { roomId: string };
  context: RouterContext;
}

export const Route = createFileRoute("/(protected)/rooms/$roomId/")({
  loader: async ({ params, context }: LoaderParams) => {
    const selectedGym = context.gymStore.getState().selectedGym;

    if (!selectedGym) {
      return { breadcrumb: "", hasActionButtons: false };
    }

    const room = await context.queryClient.ensureQueryData(
      roomQueryOptions(String(selectedGym.id), params.roomId),
    );

    return {
      breadcrumb: room?.name ?? "",
      hasActionButtons: true,
    };
  },

  pendingComponent: () => <RoomDetailSkeleton />,
  component: RouteComponent,
  errorComponent: RoomError,
});

function RouteComponent() {
  const { roomId } = Route.useParams();
  const loaderData = Route.useLoaderData();
  const navigate = Route.useNavigate();

  return (
    <ScrollableContainer>
      <div className="flex min-h-full flex-col px-6 lg:py-2">
        <MobilePageTitle
          className="mt-5 mb-4"
          onBack={() => navigate({ to: PROTECTED_ROUTES.LOGISTIC_ROOMS })}
        >
          {loaderData.breadcrumb}
        </MobilePageTitle>
        <RoomDetail roomId={Number(roomId)} />
      </div>
    </ScrollableContainer>
  );
}

function RoomError({ error }: { error: Error }) {
  const { t } = useTranslation();

  return (
    <div className="flex h-full flex-col px-6 lg:my-2">
      <ResourceError
        error={error}
        notFoundMessage={t("pages:room.errors.notFound")}
        errorMessage={t("pages:room.errors.loadingFailed", {
          message: error.message,
        })}
      />
    </div>
  );
}
